"use client";

import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Check } from "lucide-react";
export default function Origin() {
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: bodyRef,
    isVisible: bodyVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const pullQuotes = [{
    quote: "Protection is the highest form of love; we simply made it feel like a breath of fresh air.",
    attribution: "— THE B.Y.E. PRINCIPLE",
    rotation: "-rotate-2",
    tapeColor: "bg-[hsl(158,25%,92%)]"
  }, {
    quote: "The threshold is sacred. It is where the chaos of the world ends and the warmth of family begins.",
    attribution: "— SARAH MIST, FOUNDER",
    rotation: "rotate-2",
    tapeColor: "bg-[hsl(42,45%,65%)]"
  }, {
    quote: "The energy of your home is decided at the doorstep. Peace starts Before You Enter.",
    attribution: "— THE B.Y.E. CREED",
    rotation: "-rotate-2",
    tapeColor: "bg-[hsl(35,30%,96%)]"
  }];
  const benefits = [{
    id: 1,
    text: "Gentle protection at your doorstep"
  }, {
    id: 2,
    text: "Family-focused safety for every entry"
  }, {
    id: 3,
    text: "Peace of mind with every breath"
  }];
  return <section data-section-id="1556" id="origin-story-the-bye-philosophy" className="relative overflow-hidden bg-[hsl(35,30%,96%)] py-24 md:py-32 dark bg-background text-foreground">
      {}
      <svg className="absolute top-0 left-0 w-full h-4 md:h-6 text-[hsl(158,25%,92%)]" viewBox="0 0 1200 24" preserveAspectRatio="none" aria-hidden="true">
        <polygon fill="currentColor" points="0,0 40,24 80,0 120,24 160,0 200,24 240,0 280,24 320,0 360,24 400,0 440,24 480,0 520,24 560,0 600,24 640,0 680,24 720,0 760,24 800,0 840,24 880,0 920,24 960,0 1000,24 1040,0 1080,24 1120,0 1160,24 1200,0 1200,0 0,0" />
      </svg>

      {}
      <svg className="absolute bottom-0 left-0 w-full h-5 md:h-8 text-[hsl(158,25%,92%)] rotate-180" viewBox="0 0 1200 32" preserveAspectRatio="none" aria-hidden="true">
        <path fill="currentColor" d="M0,32 L0,16 Q30,0 60,16 Q90,32 120,16 Q150,0 180,16 Q210,32 240,16 Q270,0 300,16 Q330,32 360,16 Q390,0 420,16 Q450,32 480,16 Q510,0 540,16 Q570,32 600,16 Q630,0 660,16 Q690,32 720,16 Q750,0 780,16 Q810,32 840,16 Q870,0 900,16 Q930,32 960,16 Q990,0 1020,16 Q1050,32 1080,16 Q1110,0 1140,16 Q1170,32 1200,16 L1200,32 Z" />
      </svg>

      {}
      <div className="pointer-events-none absolute top-32 right-[8%] hidden md:block animate-pulse" aria-hidden="true">
        <svg width="60" height="60" viewBox="0 0 60 60" className="text-[hsl(42,45%,65%)]">
          <g stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none">
            <line x1="30" y1="6" x2="30" y2="18" />
            <line x1="30" y1="42" x2="30" y2="54" />
            <line x1="6" y1="30" x2="18" y2="30" />
            <line x1="42" y1="30" x2="54" y2="30" />
            <line x1="13" y1="13" x2="21" y2="21" />
            <line x1="39" y1="39" x2="47" y2="47" />
            <line x1="13" y1="47" x2="21" y2="39" />
            <line x1="39" y1="21" x2="47" y2="13" />
          </g>
        </svg>
      </div>
      <div className="pointer-events-none absolute top-1/2 left-[3%] hidden lg:block" aria-hidden="true">
        <svg width="50" height="50" viewBox="0 0 50 50" className="text-[hsl(42,45%,65%)]">
          <g stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none">
            <line x1="25" y1="5" x2="25" y2="15" />
            <line x1="25" y1="35" x2="25" y2="45" />
            <line x1="5" y1="25" x2="15" y2="25" />
            <line x1="35" y1="25" x2="45" y2="25" />
          </g>
        </svg>
      </div>
      <div className="pointer-events-none absolute bottom-32 right-[15%] hidden md:block" aria-hidden="true">
        <svg width="70" height="70" viewBox="0 0 70 70" className="text-[hsl(42,45%,65%)]">
          <g stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none">
            <line x1="35" y1="8" x2="35" y2="22" />
            <line x1="35" y1="48" x2="35" y2="62" />
            <line x1="8" y1="35" x2="22" y2="35" />
            <line x1="48" y1="35" x2="62" y2="35" />
            <line x1="15" y1="15" x2="25" y2="25" />
            <line x1="45" y1="45" x2="55" y2="55" />
          </g>
        </svg>
      </div>

      {}
      <div className="pointer-events-none absolute top-16 left-[28%] hidden lg:block select-none" aria-hidden="true">
        <span className="font-serif text-[20rem] leading-none text-transparent" style={{
        WebkitTextStroke: "3px hsl(158, 15%, 25% / 0.08)"
      }}>
          01
        </span>
      </div>

      <div className="relative mx-auto max-w-[1360px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div ref={headRef} className="lg:sticky lg:top-24 transition-all duration-700 ease-out" style={{
            transform: headVisible ? "translateY(0) rotate(0deg)" : "translateY(28px) rotate(-3deg)",
            opacity: headVisible ? 1 : 0
          }}>
              {}
              <div className="inline-block -rotate-3 mb-8">
                <div className="relative bg-[hsl(158,25%,92%)] border-[3px] border-[hsl(158,15%,25%)] rounded-lg px-4 py-2">
                  <span className="font-mono text-xs font-medium uppercase tracking-[0.16em] text-[hsl(158,15%,25%)]">
                    Origin / The Philosophy
                  </span>
                  {}
                  <svg className="absolute -bottom-1 -right-1 w-3 h-3 text-[hsl(158,15%,25%)]" viewBox="0 0 12 12" aria-hidden="true">
                    <path d="M0,12 L12,0 L12,12 Z" fill="currentColor" />
                  </svg>
                </div>
              </div>

              {}
              <h2 className="font-mono text-sm font-semibold uppercase tracking-[0.2em] text-[hsl(42,45%,65%)] mb-4">
                Origin: The Philosophy
              </h2>
              <h2 className="font-default text-[hsl(158,15%,25%)] mb-10" style={{
              fontSize: "clamp(2.5rem, 6vw, 5.5rem)",
              letterSpacing: "-0.025em",
              lineHeight: 0.92
            }}>
                Protecting your sanctuary <span className="italic font-light">Before You Enter</span>.
              </h2>

              {}
              <Link to="/ShopPage">
                <Button size="lg" className="bg-[hsl(42,45%,65%)] text-white border-[3px] border-[hsl(42,45%,65%)] rounded-lg px-7 py-6 h-auto font-default text-sm font-semibold uppercase tracking-[0.12em] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
                  Explore the B.Y.E. Collection
                </Button>
              </Link>

              <p className="mt-12 font-mono text-[10px] uppercase tracking-widest text-[hsl(158,10%,40%)] opacity-70">
                The transition begins here.
              </p>
            </div>
          </div>

          {}
          <div ref={bodyRef} className="lg:col-span-7">
            <div className="transition-all duration-700 ease-out" style={{
            transform: bodyVisible ? "translateY(0)" : "translateY(28px)",
            opacity: bodyVisible ? 1 : 0,
            transitionDelay: "150ms"
          }}>
              <p className="text-[hsl(158,15%,25%)] font-default mb-12" style={{
              fontSize: "clamp(1.125rem, 1.3vw, 1.375rem)",
              lineHeight: 1.55,
              letterSpacing: "-0.005em"
            }}>
                <span className="font-serif float-left mr-3 leading-none text-[hsl(158,25%,92%)] bg-[hsl(35,30%,96%)] border-[3px] border-[hsl(158,15%,25%)] rounded-lg px-3 py-1 -rotate-3 mt-1" style={{
                fontSize: "96px"
              }}>
                  B
                </span>
                .Y.E. Hygiene Mist™ is built upon the Before You Enter™ principle—a gentle promise to keep your home safe and serene. We believe that every entry is an opportunity for mindfulness, replacing harsh chemicals with a soft, protective mist that honors your family warmth. By creating a safe threshold between the world and your sanctuary, we ensure that only peace and purity cross your doorstep.
              </p>

              {}
              <div className="mb-16 grid grid-cols-1 md:grid-cols-3 gap-6">
                {benefits.map((benefit, i) => <div key={benefit.id} data-index={i} className="flex items-start gap-3">
                    <div className="mt-1 flex size-5 shrink-0 items-center justify-center rounded-lg bg-[hsl(158,25%,92%)] text-[hsl(42,45%,65%)]">
                      <Check className="size-3.5" />
                    </div>
                    <span className="font-default text-sm font-medium text-[hsl(158,15%,25%)] leading-tight">
                      {benefit.text}
                    </span>
                  </div>)}
              </div>
            </div>

            {}
            <div className="space-y-8">
              {pullQuotes.map((pq, i) => {
              return <PullQuoteCard key={i} data-index={i} quote={pq.quote} attribution={pq.attribution} rotation={pq.rotation} tapeColor={pq.tapeColor} delay={250 + i * 120} />;
            })}
            </div>

            {}
            <div className="mt-16 pt-8 border-t border-[hsl(158,10%,85%)]">
              <p className="text-[hsl(158,10%,40%)] font-default leading-relaxed italic">
                Whether you are welcoming a little one home as{" "}
                <Link to="/NewbornParentsPage" className="text-[hsl(42,45%,65%)] font-medium hover:underline">
                  newborn parents
                </Link>
                , maintaining high standards for{" "}
                <Link to="/TravelHygienePage" className="text-[hsl(42,45%,65%)] font-medium hover:underline">
                  travel hygiene
                </Link>{" "}
                on the go, or ensuring safety within{" "}
                <Link to="/ChildcareCentersPage" className="text-[hsl(42,45%,65%)] font-medium hover:underline">
                  childcare centers
                </Link>
                , our philosophy remains the same: protect the peace before you cross the line.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function PullQuoteCard({
  quote,
  attribution,
  rotation,
  tapeColor,
  delay,
  ...rest
}: {
  quote: string;
  attribution: string;
  rotation: string;
  tapeColor: string;
  delay: number;
  "data-index"?: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} {...rest} className={`relative ${rotation} bg-white border-[3px] border-[hsl(158,15%,25%)] rounded-lg p-7 md:p-9 shadow-lg hover:rotate-0 hover:-translate-y-1.5 transition-all duration-300 ease-out`} style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "" : "translateY(28px)",
    transitionDelay: `${delay}ms`
  }}>
      {}
      <div className={`absolute -top-3 -right-2 w-20 h-7 ${tapeColor} border-2 border-[hsl(158,15%,25%)] rotate-[14deg] opacity-90`} style={{
      backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 4px, hsl(158, 15%, 25% / 0.15) 4px, hsl(158, 15%, 25% / 0.15) 8px)"
    }} aria-hidden="true" />

      <blockquote>
        <p className="font-default text-[hsl(158,15%,25%)] mb-4" style={{
        fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)",
        lineHeight: 1.05,
        letterSpacing: "-0.015em"
      }}>
          &ldquo;{quote}&rdquo;
        </p>

        {}
        <svg className="w-32 h-3 mb-3 text-[hsl(158,25%,92%)]" viewBox="0 0 120 12" preserveAspectRatio="none" aria-hidden="true">
          <path d="M2,6 Q12,1 22,6 T42,6 T62,6 T82,6 T102,6 T118,6" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>

        <footer className="font-mono text-xs font-medium uppercase tracking-[0.12em] text-[hsl(158,10%,40%)]">
          {attribution}
        </footer>
      </blockquote>
    </div>;
}